import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import React, { useContext, useEffect, useState } from 'react';
import { BsTrash } from 'react-icons/bs';
import { BiAddToQueue } from 'react-icons/bi';
import { AiTwotoneEdit } from 'react-icons/ai';
import PageTitle from '../../../components/page-title/PageTitle';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import IPage from '../../../interfaces/IPage';
import { Container } from './CollaboratorList.styled';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomTablePagination from '../../../components/custom-table-pagination/CustomTablePagination';
import CustomTableSortLabel from '../../../components/custom-table-sort-label/CustomTableSortLabel';
import { useCollaboratorService } from '../../../hooks/services/useCollaboratorService';
import IEmployee from '../../../interfaces/IEmployee';
import FollowupSearchEmployee from '../../followup/FollowupSearchEmployee';
import { useNavigate } from 'react-router';
import SearchCompany from '../../../components/searchCompany/SearchCompany';
import ICompany from '../../../interfaces/ICompany';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';

interface EmployeeListProps extends IPage {}
export interface IFilterEmployeeList {
  id?: number | null;
  companyId?: number | null;
}

const CollaboratorList = (props: EmployeeListProps) => {
  window.document.title = props.title;
  const [employees, setEmployees] = useState<IEmployee[] | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('code');
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState<IEmployee | null>(null);
  const [company, setCompany] = useState<ICompany | null>(null);
  const request = useCollaboratorService();
  const toastr = useToastrVanila();
  const navigate = useNavigate();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;
    
  const isEdit = hasAccessToAction(
    ["colaboradores-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const listEmployees = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: 'asc' | 'desc',
    filter?: IFilterEmployeeList,
  ) => {
    setLoading(true);
    const id = filter?.id || employee?.id;
    // let companyId = id ? filter?.companyId || company?.id : undefined;
    const companyId = employee?.id ? null : filter?.companyId || company?.id;
    await request
      .list(perPage, currentPage + 1, orderField, orderDirection, { id, companyId })
      .then(result => {
        setEmployees([...result?.data]);
        setTotalRows(result.totalRows);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const removeCollaborator = async (id: number, employee: IEmployee) => {
    setLoading(true);
    const now = new Date();
    employee.deletedAt = now.toISOString();
    await request
      .remove(id)
      .then(async result => {
        setLoading(false);
        toastr.success('Colaborador excluído com sucesso! ');

        listEmployees(perPage, currentPage, orderField, orderDirection);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    listEmployees(perPage, currentPage, orderField, orderDirection);
  }, [employee, company]);

  const formatDate = (value: string | undefined) => {
  if (!value) return '—';
    const date = value.includes('T') ? new Date(value) : new Date(value + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatDateTime = (value: string | undefined) => {
    if (!value) return '—';
      return new Date(value).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Container>
      <PageTitle title="LISTAGEM DE COLABORADORES">
        <Tooltip title="Cadastrar novo" placement="left-start">
          <IconButton onClick={() => navigate(`/collaborator/${0}/new`)} color="inherit" disabled={!isEdit}>
            <BiAddToQueue />
          </IconButton>
        </Tooltip>
      </PageTitle>

      <Box sx={{ marginTop: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography>Filtros</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item md={3} sm={12} xs={12}>
                <SearchCompany company={company} setCompany={setCompany} />
              </Grid>
              <Grid item md={3} sm={12} xs={12}>
                <FollowupSearchEmployee
                  setEmployee={employee => {
                    setCurrentPage(0);
                    setEmployee(employee);
                    listEmployees(perPage, currentPage, orderField, orderDirection, {
                      id: employee?.id,
                      // companyId: company?.id,
                    });
                  }}
                  employee={employee}
                  company={company}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="code"
                label="Matricula"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="name"
                label="Nome"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="cpf"
                label="CPF"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="birthday"
                label="Data de Nascimento"
                handleRequest={listEmployees}
                direction={orderDirection}
                setDirection={setOrderDirection}
                setField={setOrderField}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="company_name"
                label="Empresa"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />

              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="admissionDate"
                label="Data de admissão"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="demissionDate"
                label="Data de demissão"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="city"
                label="Cidade"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="state"
                label="Estado"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="createdAt"
                label="Data de criação"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="updatedAt"
                label="Data de atualização"
                handleRequest={listEmployees}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees &&
              employees.map(employee => {
                return (
                  <TableRow key={employee.id} hover={true}>
                    <TableCell>{employee.code}</TableCell>
                    <TableCell>{employee.name}</TableCell>
                    <TableCell>{employee.cpf}</TableCell>
                    <TableCell>{formatDate(employee.birthday)}</TableCell>
                    <TableCell>
                      {employee.company?.code} - {employee.company?.name}
                    </TableCell>
                    <TableCell>{formatDate(employee.admissionDate)}</TableCell>
                    <TableCell>{formatDate(employee.demissionDate)}</TableCell>
                    <TableCell>{employee.city}</TableCell>
                    <TableCell>{employee.state}</TableCell>
                    <TableCell>{formatDate(employee.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(employee.updatedAt)}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton onClick={() => navigate(`/collaborator/${employee.id}/edit`)}>
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" placement="top">
                        <IconButton onClick={() => removeCollaborator(employee.id || 0, employee)} disabled={!isEdit}>
                          <BsTrash style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        totalRows={totalRows}
        currentPage={currentPage}
        perPage={perPage}
        handleRequest={listEmployees}
        setPerPage={setPerPage}
        setCurrentPage={setCurrentPage}
        orderField={orderField}
        orderDirection={orderDirection}
      />
    </Container>
  );
};

export default CollaboratorList;
